import apiClient from '../utils/apiClient';

/**
 * Payroll API Services
 * Quản lý bảng lương: tính toán, duyệt, thanh toán
 */

export const payrollAPI = {
  /**
   * Tính bảng lương cho một nhân viên
   * @param {Object} data - Dữ liệu tính lương
   * @param {string} data.employee_id - ID nhân viên
   * @param {string} data.month - Tháng (YYYY-MM)
   * @returns {Promise<Object>} Bảng lương đã tính (status DRAFT)
   */
  calculate: async (data) => {
    return await apiClient.post({
      url: '/payroll/calculate',
      data,
    });
  },

  /**
   * Tính bảng lương cho toàn bộ nhân viên
   * @param {Object} data - Dữ liệu tính lương
   * @param {string} data.month - Tháng (YYYY-MM)
   * @returns {Promise<Array>} Danh sách kết quả tính lương
   */
  calculateAll: async (data) => {
    return await apiClient.post({
      url: '/payroll/calculate-all',
      data,
    });
  },

  /**
   * Lấy thông tin chi tiết bảng lương
   * @param {string} id - ID bảng lương
   * @returns {Promise<Object>} Thông tin chi tiết bảng lương
   */
  getPayrollById: async (id) => {
    return await apiClient.get({
      url: `/payroll/${id}`,
    });
  },

  /**
   * Lấy chi tiết giờ làm việc của bảng lương
   * @param {string} id - ID bảng lương
   * @returns {Promise<Object>} Danh sách ca làm việc và tổng kết
   */
  getHoursDetail: async (id) => {
    return await apiClient.get({
      url: `/payroll/${id}/hours-detail`,
    });
  },

  /**
   * Lấy chi tiết điểm thưởng/phạt của bảng lương
   * @param {string} id - ID bảng lương
   * @returns {Promise<Object>} Danh sách điểm và tổng kết
   */
  getPointsDetail: async (id) => {
    return await apiClient.get({
      url: `/payroll/${id}/points-detail`,
    });
  },

  /**
   * Lấy lịch sử bảng lương của nhân viên
   * @param {string} employeeId - ID nhân viên
   * @param {Object} params - Tham số phân trang
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách bảng lương và metadata
   */
  getEmployeeHistory: async (employeeId, params = {}) => {
    const { page = 1, limit = 10 } = params;
    return await apiClient.get({
      url: `/payroll/employee/${employeeId}/history`,
      params: { page, limit },
    });
  },

  /**
   * So sánh bảng lương tháng hiện tại vs tháng trước
   * @param {string} employeeId - ID nhân viên
   * @param {Object} params - Tham số
   * @param {string} params.month - Tháng (YYYY-MM)
   * @returns {Promise<Object>} Dữ liệu so sánh
   */
  compareWithPreviousMonth: async (employeeId, params = {}) => {
    const { month } = params;
    return await apiClient.get({
      url: `/payroll/employee/${employeeId}/compare`,
      params: { month },
    });
  },

  /**
   * Duyệt bảng lương
   * @param {string} id - ID bảng lương
   * @param {Object} data - Dữ liệu
   * @param {string} data.current_user_id - ID người duyệt
   * @returns {Promise<Object>} Bảng lương sau khi duyệt
   */
  approve: async (id, data) => {
    return await apiClient.post({
      url: `/payroll/${id}/approve`,
      data,
    });
  },

  /**
   * Đánh dấu đã thanh toán
   * @param {string} id - ID bảng lương
   * @param {Object} data - Dữ liệu
   * @param {string} data.current_user_id - ID người đánh dấu
   * @returns {Promise<Object>} Bảng lương sau khi đánh dấu
   */
  markPaid: async (id, data) => {
    return await apiClient.post({
      url: `/payroll/${id}/mark-paid`,
      data,
    });
  },

  /**
   * #175506 — Cập nhật ảnh chuyển khoản của bảng lương (bắt buộc trước khi đánh dấu đã thanh toán)
   * @param {string} id - ID bảng lương
   * @param {string[]} evidenceUrls - Danh sách URL ảnh (mảng rỗng = xoá hết)
   * @returns {Promise<Object>} Bảng lương sau khi cập nhật
   */
  updatePaymentEvidence: async (id, evidenceUrls) => {
    return await apiClient.post({
      url: `/payroll/${id}/payment-evidence`,
      data: { evidence_urls: evidenceUrls },
    });
  },

  /**
   * Export bảng lương
   * @param {string} id - ID bảng lương
   * @param {Object} params - Tham số
   * @param {string} params.format - Định dạng (csv, pdf)
   * @param {boolean} params.send_telegram - Gửi qua Telegram (nếu true: thành công trả JSON, lỗi trả file CSV; nếu false: luôn trả file CSV)
   * @returns {Promise<Object|Blob>} Thông tin export (JSON) hoặc Blob file (CSV)
   */
  exportPayroll: async (id, params = {}) => {
    const { format = 'csv', send_telegram = false } = params;
    const blob = await apiClient.get({
      url: `/payroll/${id}/export`,
      params: {
        format,
        ...(send_telegram ? { send_telegram: true } : {}),
      },
      // Luôn dùng blob để có thể nhận cả JSON và file CSV
      responseType: 'blob',
    });

    // Nếu không gửi Telegram, luôn trả về file CSV
    if (!send_telegram) {
      return blob;
    }

    // Nếu gửi Telegram, cần kiểm tra xem là JSON hay CSV
    // Thử parse blob thành text rồi parse JSON
    try {
      const text = await blob.text();
      const jsonData = JSON.parse(text);

      // Nếu parse được JSON và có sent_to_telegram, đó là response thành công
      if (jsonData && typeof jsonData === 'object' && jsonData.sent_to_telegram !== undefined) {
        return jsonData;
      }

      // Nếu không phải JSON hợp lệ, tạo lại blob từ text (trường hợp CSV)
      return new Blob([text], { type: 'text/csv' });
    } catch (error) {
      // Nếu không parse được JSON, đó là file CSV
      return blob;
    }
  },

  /**
   * Lấy danh sách bảng lương với filters
   * @param {Object} params - Tham số lọc và phân trang
   * @param {string} params.employee_id - ID nhân viên
   * @param {string} params.employee_name - Tên nhân viên (tìm kiếm)
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.month - Tháng (YYYY-MM)
   * @param {string} params.status - Trạng thái (DRAFT, PENDING_APPROVAL, APPROVED, CONFIRMED, PAID)
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách bảng lương và metadata
   */
  getPayrolls: async (params = {}) => {
    const { employee_id, employee_name, branch_id, month, status, page = 1, limit = 10 } = params;
    return await apiClient.get({
      url: '/payroll',
      params: {
        ...(employee_id ? { employee_id } : {}),
        ...(employee_name ? { employee_name } : {}),
        ...(branch_id ? { branch_id } : {}),
        ...(month ? { month } : {}),
        ...(status ? { status } : {}),
        page,
        limit,
      },
    });
  },
};

/**
 * #175506 — Ứng lương: khoản tiền nhân viên đã ứng trước, gắn với 1 kỳ lương (YYYY-MM).
 * Khi tính lương kỳ đó, tổng khoản ứng bị trừ vào lương thực nhận và hiện dòng "Đã ứng lương".
 */
export const salaryAdvanceAPI = {
  /**
   * @param {Object} params
   * @param {string} [params.employee_id]
   * @param {string} [params.month] - YYYY-MM
   * @returns {Promise<Array>} Danh sách khoản ứng
   */
  list: async (params = {}) => {
    const { employee_id, month } = params;
    return await apiClient.get({
      url: '/salary-advances',
      params: {
        ...(employee_id ? { employee_id } : {}),
        ...(month ? { month } : {}),
      },
    });
  },

  /**
   * @param {Object} data
   * @param {string} data.employee_id
   * @param {string} data.month - YYYY-MM
   * @param {number} data.amount - VND, số nguyên dương
   * @param {string} [data.note]
   */
  create: async (data) => {
    return await apiClient.post({ url: '/salary-advances', data });
  },

  remove: async (id) => {
    return await apiClient.delete({ url: `/salary-advances/${id}` });
  },

  /**
   * #175506 — Hạn mức ứng lương của 1 nhân viên trong 1 kỳ: trần (100% lương thực nhận của kỳ,
   * theo tham số cấu hình), đã ứng, còn ứng được và cảnh báo khi đang vượt trần.
   * Lấy thẳng từ máy chủ để màn hình không tự tính ra con số khác với chỗ chặn.
   * @param {Object} params
   * @param {string} params.employee_id
   * @param {string} params.month - YYYY-MM
   */
  quota: async ({ employee_id, month }) => {
    return await apiClient.get({
      url: '/salary-advances/quota',
      params: { employee_id, month },
    });
  },

  /** Link/yêu cầu ứng lương nhân viên tự gửi qua link riêng (lọc theo trạng thái). */
  listRequests: async (params = {}) => {
    return await apiClient.get({ url: '/salary-advance-requests', params });
  },

  /** Tạo link xin ứng lương cho 1 nhân viên — link mới thay link cũ chưa dùng. */
  createRequestLink: async (employee_id) => {
    return await apiClient.post({ url: '/salary-advance-requests/links', data: { employee_id } });
  },

  /** Đã chuyển tiền ⇒ duyệt; `amount` bỏ trống = đúng số nhân viên xin. */
  approveRequest: async (id, data = {}) => {
    return await apiClient.post({ url: `/salary-advance-requests/${id}/approve`, data });
  },

  rejectRequest: async (id, data = {}) => {
    return await apiClient.post({ url: `/salary-advance-requests/${id}/reject`, data });
  },

  revokeRequestLink: async (id) => {
    return await apiClient.post({ url: `/salary-advance-requests/${id}/revoke`, data: {} });
  },
};
